import type { SortBy } from '@/components/molecules/SortByDropdown';
import type { OverviewCage } from '@/domain/client';
import { compareCageNames } from '@/utils/compareCageNames';

function compareOverviewCageNames(a: OverviewCage, b: OverviewCage): number {
    return compareCageNames(a.cageName, b.cageName);
}

type Selector = (cage: OverviewCage) => number | undefined;

const selectors: Record<Exclude<SortBy, 'name-asc'>, Selector> = {
    'lastMeasurement-desc': (cage) => parseDay(cage.cageData.weight?.day),
    'lastMeasurement-asc': (cage) => parseDay(cage.cageData.weight?.day),
    'weight-desc': (cage) => cage.cageData.weight?.livingWeight,
    'weight-asc': (cage) => cage.cageData.weight?.livingWeight,
    'lice-desc': (cage) => cage.cageData.lice?.femaleLegacyAvg,
    'lice-asc': (cage) => cage.cageData.lice?.femaleLegacyAvg,
};

function parseDay(day: string | undefined): number | undefined {
    if (!day) return undefined;
    const time = Date.parse(day);
    return Number.isNaN(time) ? undefined : time;
}

function isMissing(value: number | undefined): value is undefined {
    return value === undefined || value === null || Number.isNaN(value);
}

export function sortCages(cages: OverviewCage[], sortBy: SortBy): OverviewCage[] {
    if (sortBy === 'name-asc') {
        return [...cages].sort(compareOverviewCageNames);
    }

    const selector = selectors[sortBy];
    const direction = sortBy.endsWith('-desc') ? -1 : 1;

    return [...cages].sort((a, b) => {
        const va = selector(a);
        const vb = selector(b);
        const aMissing = isMissing(va);
        const bMissing = isMissing(vb);

        if (aMissing && bMissing) return compareOverviewCageNames(a, b);
        if (aMissing) return 1;
        if (bMissing) return -1;

        if (va === vb) return compareOverviewCageNames(a, b);
        return va < vb ? -1 * direction : 1 * direction;
    });
}
